import {compact, objectFromEntries, entries} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  OligoAwareFolderItemType,
  SchemalessBulkImportSupportedOligoAwareTypes,
  TagSchema,
} from 'constants/RegistryConstants';
import SampleType, {CONTAINER_SAMPLE, SAMPLE_TYPE_DISPLAY} from 'constants/SampleType';
import * as FileUtil from 'folder-items/FileUtil';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {
  ComplexPolymerSchemaConfigGraphQL,
  isOligoDuplexConfigGraphQLType,
} from 'modbio/complex-polymers/ComplexPolymerUtil';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import preferences from 'Preferences';
import {ProcessOutputSchema, ProcessTaskSchema} from 'processes/ProcessConstants';
import readProcessOutputSchemaFromCache from 'processes/readProcessOutputSchemaFromCache';
import readProcessTaskSchemaFromCache from 'processes/readProcessTaskSchemaFromCache';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import AssayStore from 'results/AssayStore';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {
  BoxSchema,
  ContainerSchema,
  LocationSchema,
  PlateSchema,
} from 'samples/storables/StorableSchemaConstants';
import * as ComputedFieldUtil from 'schemas/fields/computed/ComputedFieldUtil';
import {isRequiredLinkSample, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {AnonymousSchema} from 'schemas/SchemaConstants';
import {getTranslatedTagSchemaFromCache} from 'schemas/util/getTagSchemaFromCache';
import EntityStore from 'stores/EntityStore';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import {getTagSchemaOligoAwareFolderItemType} from 'tag-schema/util/TagSchemaUtil';
import {GRAY_DARKER, ORANGE, RED} from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {capitalize, listToReadable, typeToName} from 'util/NameUtil';
import pick from 'util/pick';
import sortedBy from 'util/sortedBy';
import {SampleGroupSchema} from 'workflows/WorkflowConstants';

import {
  COLUMN_TYPE,
  COLUMN_TYPE_TO_HELP_TEXT,
  COLUMN_TYPE_TO_NAME,
  COLUMN_TYPES_ALLOWING_MULTIPLE_VALUES,
  COLUMN_TYPES_PRIORITIZED_OVER_SCHEMA_FIELDS,
  COLUMN_TYPES_TO_CHECK_FOR_DERIVED_COLUMNS,
  ColumnMappingSource,
  ColumnTransform,
  COMPLEX_POLYMER_COLUMN_TYPES,
  DerivedColumn,
  DerivedColumnsMapping,
  ErrorData,
  IMPORT_TYPE,
  LINK_TAG_ERROR_CODES,
  MOLECULE_COLUMN_TYPES,
  NON_BLOCKING_ERROR_CODES,
  OPTIONS,
  RowErrorCode,
  SCHEMA_FIELD_COLUMN_TYPES,
  SelectedSchemaIds,
  SpreadsheetBulkImportPreferences,
  SpreadsheetColumn,
  SpreadsheetValidationError,
  SpreadsheetValidationErrorInfo,
  TaskState,
  TRANSFER_PRIMARY_STORABLE_INPUT_COLUMN_TYPES,
} from './SpreadsheetBulkImportConstants';

// TODO(BNCH-108932): Improve maintainability of these (and following negative-) regexes
// One option: convert this to a list of tuples to make ordering explicit, and remove the negative regexes
export const COLUMN_TYPE_TO_RE: {[key in COLUMN_TYPE]?: RegExp} = {
  [COLUMN_TYPE.INPUT_CONCENTRATION_VALUE]:
    /^\s*((source)?\s*(batch|entity)?)?\s*(conc|concs|concentration|concentrations)\s*$/i,
  [COLUMN_TYPE.FINAL_CONCENTRATION_VALUE]:
    /^\s*((final)?\s*(entity)?)?\s*(conc|concs|concentration|concentrations)\s*$/i,
  [COLUMN_TYPE.CONCENTRATION_UNITS]:
    /^\s*(final)?\s*((source)?\s*(batch|entity)?)?\s*(conc|concentration)\s*(unit)s?\s*/i,
  [COLUMN_TYPE.STAGE_RUN_SAMPLE_NAME]: /\s*(sample)s?\s*/i,
  [COLUMN_TYPE.STAGE_RUN_NAME]: /\s*(stage run)s?\s*/i,
  [COLUMN_TYPE.REGISTRATION_TABLE_EXISTING_BIOENTITY]: /^(entity)\s*$/i,
  // Must be kept above FILENAME to avoid matching on the word "name" in the FILENAME regex
  [COLUMN_TYPE.NEW_BIOENTITY_NAME]: /^\s*((final |new )(entity )?name)s?\s*/i,
  [COLUMN_TYPE.FILENAME]: /\s*((entity|sample) name|name)s?\s*/i,
  [COLUMN_TYPE.BATCH_NAME]: /\s*(source\s+)?(batch|sample)( id)?s?\s*/i,
  [COLUMN_TYPE.INPUT_BATCH_NAME]: /\s*(name|(source\s+)?batch( id)?)s?\s*/i,
  [COLUMN_TYPE.INPUT_BIOENTITY_LABEL]: /\s*((source\s+)?entity( name)?)s?\s*/i,
  [COLUMN_TYPE.NUMBER_OF_CONTAINERS]: /\s*((num(ber)?\s*(of)?)\s+(container)s?)\s*/i,
  [COLUMN_TYPE.BARCODE]: /^((current |old )?barcode|container( barcode)?)s?/i,
  [COLUMN_TYPE.NEW_BARCODE]: /^((final |new )barcode)s?/i,
  [COLUMN_TYPE.INPUT_CONTAINER_BARCODE]: /\s*(((source|input)\s+)container( barcode)?)s?\s*/i,
  [COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE]: /\s*(((dest(ination)?|output)\s+)container( barcode)?)s?\s*/i,
  [COLUMN_TYPE.OUTPUT_GRID_BARCODE]: /\s*(dest(ination)? (matrix\s+)?(plate|box)( barcode)?)s?\s*/i,
  [COLUMN_TYPE.OUTPUT_GRID_COORDINATES]: /\s*(dest(ination)? (position|well|coordinate))s?\s*/i,
  [COLUMN_TYPE.INPUT_GRID_BARCODE]: /\s*(((source|input)\s+)?(plate|box)( barcode)?)s?\s*/i,
  [COLUMN_TYPE.INPUT_GRID_COORDINATES]: /\s*(source ([cw]ell|position))s?\s*/i,
  // Do not match column headers containing the word "final"
  [COLUMN_TYPE.TRANSFER_QUANTITY]:
    /^\s*(?!.*final)(transfer)?\s*(vol|amt|qty|volume|amount|quantity|mass)\s*/i,
  // "final" is not required because it can fall through to this in flows that only have final quantity and
  // not transfer quantity (update containers)
  [COLUMN_TYPE.FINAL_QUANTITY_VALUE]: /\s*(final)?\s*(vol|amt|qty|volume|amount|quantity|mass)\s*/i,
  // Require both "add" and "deduct" to indicate change in value.
  [COLUMN_TYPE.ADD_DEDUCT_AMOUNT]: /\s*(add)(\b|_).*(deduct)\s*/i,
  [COLUMN_TYPE.QUANTITY_UNITS]: /\s*((vol|amt|qty|volume|amount|mass|quantity)?\s*unit)s?\s*/i,
  [COLUMN_TYPE.DESCRIPTION]: /\s*(note|description|desc|info|information)s?\s*/i,
  [COLUMN_TYPE.RESIDUES]: /\s*(amino\s*acids|bases|residues|sequence)s?\s*/i,
  [COLUMN_TYPE.HELM]: /\s*(helm)\s*/i,
  [COLUMN_TYPE.IDT]: /\s*(idt)\s*/i,
  [COLUMN_TYPE.STORABLE_NAME]: /\s*(name)s?\s*/i,
  [COLUMN_TYPE.PARENT_STORABLE_GRID_COORDINATES]:
    /\s*((parent)?(storage|plate|box)?\s*(position|well|coordinate))s?\s*/i,
  // Exclude matches with capacity, quantity, children or schema to avoid matching the location capacity config columns
  [COLUMN_TYPE.PARENT_STORABLE_BARCODE]:
    /\s*(((parent)?(storage|plate|location|box)? barcode)s?|(storage|plate|location|box)s?)(?!.*(capacity|quantity|children|schema))\s*/i,

  // Sample control roles
  [COLUMN_TYPE.SAMPLE_OWNERS]: /\s*(sample )?(owner)s?\s*/i,
  [COLUMN_TYPE.SAMPLE_RESTRICTION_STATUS]: /\s*(sample )?(restriction )?status\s*/i,
  [COLUMN_TYPE.SAMPLE_AUTHORIZED_USERS]: /\s*((sample )?(authorized|restricted) (sample )?user)s?\s*/i,

  // Location capacity
  [COLUMN_TYPE.ALLOWS_NON_LOCATION_CHILDREN]:
    /\s*(allow|allows) ((non |non-)location )?(children|box|plate|container|inventory)s?\s*/i,
  [COLUMN_TYPE.LOCATION_CAPACITY]: /\s*(location )?(capacity|limit|quantity)\s*/i,
  [COLUMN_TYPE.ALLOWED_CHILD_STORABLE_SCHEMAS]:
    /\s*((allowed)?( child|children)?\s*(inventory|container|plate|box)? schema)s?\s*/i,

  // Expiration date & freeze/thaw count
  // Does not require "override" to be present; should be lower priority than a direct expiration date column
  [COLUMN_TYPE.EXPIRATION_DATE_OVERRIDE]: /^\s*expiration/i,
  [COLUMN_TYPE.FREEZE_THAW_COUNT]: /^\s*(freeze|thaw)/i,

  // Mixture ingredients
  [COLUMN_TYPE.INGREDIENT_MIXTURE_LABEL]: /^\s*mixture\s*/i,
  [COLUMN_TYPE.INGREDIENT_COMPONENT_BIOENTITY_LABEL]: /\s*component\s*/i,
  [COLUMN_TYPE.INGREDIENT_UPDATED_COMPONENT_BIOENTITY_LABEL]: /\s*(new |updated )component\s*/i,
  [COLUMN_TYPE.INGREDIENT_CATALOG_IDENTIFIER]: /\s*catalog\s*/i,
  [COLUMN_TYPE.INGREDIENT_AMOUNT]: /\s*(amount|amt|volume|mass)\s*/i,
  [COLUMN_TYPE.INGREDIENT_UNITS]: /\s*units?\s*/i,
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_TEXT]: /\s*(source|(component )?lot) text\s*/i,
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_BIOENTITY_LABEL]: /\s*(component )?lot entity\s*/i,
  [COLUMN_TYPE.INGREDIENT_COMPONENT_LOT_CONTAINER_BARCODE]: /\s*(component )?lot container\s*/i,
  [COLUMN_TYPE.INGREDIENT_NOTES]: /\s*(notes?|comments?)\s*/i,

  // Processes
  [COLUMN_TYPE.PROCESS_TASK_ASSIGNEE]: /\s*assignee\s*/i,
  [COLUMN_TYPE.PROCESS_TASK]: /\s*task\s*/i,

  // Chemistry
  [COLUMN_TYPE.SMILES]: /\s*(smiles)\s*/i,
  [COLUMN_TYPE.MOLFILE]: /\s*(molfile)\s*/i,

  // Studies
  [COLUMN_TYPE.STUDY_LABELS]: /\s*(study)(( label| name| id)s?)?\s*/i,

  // Trial subjects
  [COLUMN_TYPE.TRIAL_SUBJECT_NAME]: /(\b|_)+subjects?[\s_]+names?(\b|_)+/i,
};

// TODO(BNCH-108932): Improve maintainability of these (and previous) regexes
// One option: convert COLUMN_TYPE_TO_RE to a list of tuples to make ordering explicit, and remove these
export const COLUMN_TYPE_TO_NEGATIVE_RE: {[key in COLUMN_TYPE]?: RegExp} = {
  [COLUMN_TYPE.INPUT_CONTAINER_BARCODE]: /\s*(dest(ination)?|output)\s*/i,
  [COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE]: /\s*(source|input)\s*/i,
  [COLUMN_TYPE.PARENT_STORABLE_BARCODE]: /\s*container\s*/i,
  [COLUMN_TYPE.TRANSFER_QUANTITY]: /\s*unit\s*/i,
  [COLUMN_TYPE.FINAL_QUANTITY_VALUE]: /\s*unit\s*/i,
  [COLUMN_TYPE.INGREDIENT_COMPONENT_BIOENTITY_LABEL]: /\s*(new |updated |component (lot|text))\s*/i,
  [COLUMN_TYPE.INGREDIENT_UPDATED_COMPONENT_BIOENTITY_LABEL]: /\s*component (lot|text)\s*/i,
  [COLUMN_TYPE.BARCODE]: /\s*name\s*/i,
  [COLUMN_TYPE.FILENAME]: /(\b|_)+(container|subject)s?(\b|_)+/i,
  [COLUMN_TYPE.STORABLE_NAME]: /(\b|_)+subjects?(\b|_)+/i,
};

// Keep in sync with values in benchling/molbio/vendor_syntaxes/idt.py
export const COLUMN_TRANSFORM_TO_RE: {[key in ColumnTransform]: RegExp} = {
  [ColumnTransform.IDT_3MOD]: /\s*3\s*(prime|')?\s*(modification|mod|linker|conjugate|handle)\s*/i,
  [ColumnTransform.IDT_5MOD]: /\s*5\s*(prime|')?\s*(modification|mod|linker|conjugate|handle)\s*/i,
  [ColumnTransform.CHEMICAL_STRUCTURE_SALTS]: /\s*(salt)\s*/i,
};

function normalize(name: string): string {
  return name.toLowerCase().replace(/\s/g, '');
}

function guessTypeForColumnType(
  normalizedColumnName: string,
  columnType: COLUMN_TYPE,
  schemaFieldsByNormalizedNames: Map<string, SchemaField>,
  validSchemaFieldIds?: Array<string>
): {type: COLUMN_TYPE; schemaField: SchemaField} | null {
  if (schemaFieldsByNormalizedNames) {
    const schemaField = schemaFieldsByNormalizedNames.get(normalizedColumnName);
    if (schemaField && (!validSchemaFieldIds || validSchemaFieldIds.includes(schemaField.apiId))) {
      return {
        type: columnType,
        schemaField,
      };
    }
  }
  return null;
}

function guessTypeForMixtureIngredients(
  validColumnTypes: Array<COLUMN_TYPE>,
  columnName: string
): {type: COLUMN_TYPE} | null {
  if (!columnName) {
    return null;
  }
  for (const columnType of validColumnTypes) {
    assert(
      columnType === COLUMN_TYPE.IGNORE || COLUMN_TYPE_TO_RE.hasOwnProperty(columnType),
      'Column types should have regexes defined.'
    );
    if (
      COLUMN_TYPE_TO_NEGATIVE_RE.hasOwnProperty(columnType) &&
      COLUMN_TYPE_TO_NEGATIVE_RE[columnType]!.test(columnName)
    ) {
      continue;
    }
    if (COLUMN_TYPE_TO_RE[columnType]?.test(columnName)) {
      return {type: columnType};
    }
  }
  return null;
}

function guessTypeFromColumnNameRegexes(
  validColumnTypes: Array<COLUMN_TYPE>,
  columnName: string
): {type: COLUMN_TYPE} | null {
  for (const columnType of Object.keys(COLUMN_TYPE_TO_RE) as Array<COLUMN_TYPE>) {
    if (!validColumnTypes.includes(columnType)) {
      continue;
    }
    if (
      COLUMN_TYPE_TO_NEGATIVE_RE.hasOwnProperty(columnType) &&
      COLUMN_TYPE_TO_NEGATIVE_RE[columnType]?.test(columnName)
    ) {
      continue;
    }
    if (COLUMN_TYPE_TO_RE[columnType]?.test(columnName)) {
      return {type: columnType};
    }
  }
  return null;
}

function guessTypeFromColumnNames(
  validColumnTypes: Array<COLUMN_TYPE>,
  columnName: string
): {type: COLUMN_TYPE} | null {
  for (const columnType of Object.keys(COLUMN_TYPE_TO_NAME) as Array<COLUMN_TYPE>) {
    if (!validColumnTypes.includes(columnType)) {
      continue;
    }
    // Skip schema field types here - this function only returns {type} without the required
    // `schemaField` object. Schema fields are properly matched in guessTypeForColumnType(),
    // which looks up actual schema fields and returns {type, schemaField} with the object populated.
    if (SCHEMA_FIELD_COLUMN_TYPES.includes(columnType)) {
      continue;
    }
    if (normalize(COLUMN_TYPE_TO_NAME[columnType]!) === normalize(columnName)) {
      return {type: columnType};
    }
  }
  return null;
}

function guessType(
  validColumnTypes: Array<COLUMN_TYPE>,
  columnName: string,
  columnIndex: number,
  schemaFieldMapsByColumnType: Map<COLUMN_TYPE, Map<string, SchemaField>>,
  dataTableColumns: {tableColumnNames: Array<string>; dataTableKeyFields: Array<COLUMN_TYPE>},
  validSchemaFieldIds?: Array<string>
):
  | {type: COLUMN_TYPE; schemaField: SchemaField}
  | {type: COLUMN_TYPE; sampleType?: SampleType | typeof CONTAINER_SAMPLE}
  | {type: COLUMN_TYPE.DATA_TABLE_MATCHER | COLUMN_TYPE.DATA_TABLE_VALUE; tableColumnName?: string}
  | {type: COLUMN_TYPE.CUSTOM_MOD_SYNTAX; customSyntaxId: string}
  | null {
  /*
   *  There can be conflicts between the column types (e.g., a schema field named "Description"). The
   *  column matching heuristic obeys the following priorities:
   *  1. Schema fields
   *  2. Table columns
   *  3. Column name regexes, `COLUMN_TYPE_TO_RE`
   *  4. If first column, choose primary column type. Otherwise, return CUSTOM_FIELD if valid, else null.
   */
  if (!columnName.length) {
    return {type: COLUMN_TYPE.IGNORE};
  }

  const normalizedColumnName = normalize(columnName);

  if (validColumnTypes.includes(COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD)) {
    let normalizedColumnNameForContainer = null;
    if (normalizedColumnName.includes('container')) {
      normalizedColumnNameForContainer = normalize(normalizedColumnName.replace('container', ''));
    }
    for (const [key, value] of schemaFieldMapsByColumnType) {
      const guessedType = guessTypeForColumnType(normalizedColumnName, key, value, validSchemaFieldIds);
      if (guessedType) {
        assert(
          guessedType.schemaField.requiredLink != null &&
            isRequiredLinkSample(guessedType.schemaField.requiredLink),
          'Expected to receive a sample group schema field'
        );
        return {
          ...guessedType,
          sampleType: guessedType.schemaField.requiredLink.sampleType,
        };
      }
      if (normalizedColumnNameForContainer) {
        const guessedContainerType = guessTypeForColumnType(
          normalizedColumnNameForContainer,
          key,
          value,
          validSchemaFieldIds
        );
        if (guessedContainerType) {
          if (validColumnTypes.includes(COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD)) {
            return {
              ...guessedContainerType,
              sampleType: CONTAINER_SAMPLE,
            };
          }
        }
      }
    }
  }

  // For cases when we want to prioritize column name regexes over schema fields
  const prioritizedColumnTypes = _.intersection(
    validColumnTypes,
    COLUMN_TYPES_PRIORITIZED_OVER_SCHEMA_FIELDS
  );
  const guessedTypeFromPrioritizedColumns = guessTypeFromColumnNameRegexes(
    prioritizedColumnTypes,
    columnName
  );
  if (guessedTypeFromPrioritizedColumns != null) {
    return guessedTypeFromPrioritizedColumns;
  }

  // Schema columns
  for (const [key, value] of schemaFieldMapsByColumnType) {
    const guessedType = guessTypeForColumnType(normalizedColumnName, key, value, validSchemaFieldIds);
    if (guessedType) {
      return guessedType;
    }
  }
  // Table columns
  if (validColumnTypes.includes(COLUMN_TYPE.DATA_TABLE_MATCHER)) {
    const {dataTableKeyFields} = dataTableColumns;
    for (const dataTableKeyField of dataTableKeyFields) {
      if (normalize(COLUMN_TYPE_TO_NAME[dataTableKeyField]!) === normalizedColumnName) {
        return {type: COLUMN_TYPE.DATA_TABLE_MATCHER, tableColumnName: dataTableKeyField};
      }
    }
  }
  if (validColumnTypes.includes(COLUMN_TYPE.DATA_TABLE_VALUE)) {
    const {tableColumnNames} = dataTableColumns;
    for (const tableColumnName of tableColumnNames) {
      if (normalize(tableColumnName) === normalizedColumnName) {
        return {type: COLUMN_TYPE.DATA_TABLE_VALUE, tableColumnName};
      }
    }
  }
  if (validColumnTypes.includes(COLUMN_TYPE.CUSTOM_MOD_SYNTAX)) {
    const sortedCustomSyntaxEntries = sortedBy(
      [...entries(BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID)],
      [{accessor: ([, syntaxName]) => syntaxName, naturalSort: true}]
    );
    for (const [syntaxId, syntaxName] of sortedCustomSyntaxEntries) {
      if (normalize(syntaxName) === normalizedColumnName) {
        return {type: COLUMN_TYPE.CUSTOM_MOD_SYNTAX, customSyntaxId: syntaxId};
      }
    }
  }
  // Column name regexes
  const guessedTypeFromColumnNameRegexes = guessTypeFromColumnNameRegexes(validColumnTypes, columnName);
  if (guessedTypeFromColumnNameRegexes != null) {
    return guessedTypeFromColumnNameRegexes;
  }
  const guessedTypeFromColumnNames = guessTypeFromColumnNames(validColumnTypes, columnName);
  if (guessedTypeFromColumnNames != null) {
    return guessedTypeFromColumnNames;
  }

  // If nothing matches first column, pick a primary column
  if (columnIndex === 0) {
    const validPrimaryColumnTypes = [
      // Prioritize stage run samples over stage runs
      COLUMN_TYPE.STAGE_RUN_SAMPLE_NAME,
      // Prioritize data table primary samples over stage runs
      COLUMN_TYPE.FILENAME,
      COLUMN_TYPE.BATCH_NAME,
      COLUMN_TYPE.STAGE_RUN_NAME,
      COLUMN_TYPE.BARCODE,
      // Registration table column types
      COLUMN_TYPE.REGISTRATION_TABLE_EXISTING_BIOENTITY,
      // Transfer table column types
      COLUMN_TYPE.INPUT_CONTAINER_BARCODE,
      COLUMN_TYPE.INPUT_GRID_BARCODE,
      // For non-transfer-table transfers
      COLUMN_TYPE.INPUT_BIOENTITY_LABEL,
      COLUMN_TYPE.INPUT_BATCH_NAME,
      COLUMN_TYPE.PARENT_STORABLE_BARCODE,
    ].filter((columnType) => validColumnTypes.includes(columnType));
    if (validPrimaryColumnTypes.length) {
      return {type: validPrimaryColumnTypes[0]};
    }
  }

  if (validColumnTypes.includes(COLUMN_TYPE.CUSTOM_FIELD)) {
    return {type: COLUMN_TYPE.CUSTOM_FIELD};
  }
  return null;
}

function getMessageSpan(message: string, icon: BenchlingIconName): JSX.Element {
  return (
    <span>
      <BenchlingIcon
        icon={icon}
        color={icon === 'error' ? RED : icon === 'warning' ? ORANGE : GRAY_DARKER}
        className="u-tiny-spacing-right"
      />
      {message}
    </span>
  );
}

export function getTagSchema(selectedSchemaIds: SelectedSchemaIds): TagSchema | null {
  return getTranslatedTagSchemaFromCache(selectedSchemaIds.tagSchemaId);
}

export function getContainerSchema(selectedSchemaIds: SelectedSchemaIds): ContainerSchema | null {
  return SampleSchemaStore.getContainerSchema(selectedSchemaIds.containerSchemaId!);
}

export function getPlateSchema(selectedSchemaIds: SelectedSchemaIds): PlateSchema | null {
  return SampleSchemaStore.getPlateSchema(selectedSchemaIds.plateSchemaId!);
}

export function getLocationSchema(selectedSchemaIds: SelectedSchemaIds): LocationSchema | null {
  return SampleSchemaStore.getLocationSchema(selectedSchemaIds.locationSchemaId!);
}

export function getBoxSchema(selectedSchemaIds: SelectedSchemaIds): BoxSchema | null {
  return SampleSchemaStore.getBoxSchema(selectedSchemaIds.boxSchemaId!);
}

export function getSampleGroupSchema(selectedSchemaIds: SelectedSchemaIds): SampleGroupSchema | null {
  return AssayStore.getSampleGroupSchema(selectedSchemaIds.sampleGroupSchemaId!);
}

export function getResultSchema(selectedSchemaIds: SelectedSchemaIds): AssaySchema | null {
  return AssayStore.getAssaySchema(selectedSchemaIds.resultSchemaId!);
}

export function getProcessTaskSchema(selectedSchemaIds: SelectedSchemaIds): ProcessTaskSchema | null {
  return readProcessTaskSchemaFromCache(selectedSchemaIds.processTaskSchemaId!);
}

export function getProcessOutputSchema(selectedSchemaIds: SelectedSchemaIds): ProcessOutputSchema | null {
  return readProcessOutputSchemaFromCache(selectedSchemaIds.processOutputSchemaId!);
}

export function getTrialSubjectSchema(selectedSchemaIds: SelectedSchemaIds): TagSchema | null {
  return getTranslatedTagSchemaFromCache(selectedSchemaIds.trialSubjectSchemaId);
}

export function getSchemaFromColumnType(
  columnType: COLUMN_TYPE,
  selectedSchemaIds: SelectedSchemaIds
): AnonymousSchema | null {
  switch (columnType) {
    case COLUMN_TYPE.SCHEMA_FIELD:
      return getTagSchema(selectedSchemaIds);
    case COLUMN_TYPE.CONTAINER_SCHEMA_FIELD:
      return getContainerSchema(selectedSchemaIds);
    case COLUMN_TYPE.PLATE_SCHEMA_FIELD:
      return getPlateSchema(selectedSchemaIds);
    case COLUMN_TYPE.LOCATION_SCHEMA_FIELD:
      return getLocationSchema(selectedSchemaIds);
    case COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD:
      return getSampleGroupSchema(selectedSchemaIds);
    case COLUMN_TYPE.RESULT_SCHEMA_FIELD:
      return getResultSchema(selectedSchemaIds);
    case COLUMN_TYPE.PROCESS_TASK_SCHEMA_FIELD:
      return getProcessTaskSchema(selectedSchemaIds);
    case COLUMN_TYPE.PROCESS_OUTPUT_SCHEMA_FIELD:
      return getProcessOutputSchema(selectedSchemaIds);
    case COLUMN_TYPE.TRIAL_SUBJECT_SCHEMA_FIELD:
      return getTrialSubjectSchema(selectedSchemaIds);
    default:
      assert(columnType === COLUMN_TYPE.BOX_SCHEMA_FIELD, 'Unexpected schema field column type');
      return getBoxSchema(selectedSchemaIds);
  }
}

export function getExampleImageURLForTagSchema(tagSchema: TagSchema | null): string {
  if (tagSchema) {
    switch (tagSchema.folderItemType) {
      case ModelTypeConstants.PROTEIN:
        return '/static/img/spreadsheet-import-example-protein.png';
      case ModelTypeConstants.SEQUENCE:
        return '/static/img/spreadsheet-import-example-sequence.png';
      default:
        break;
    }
  }
  return '/static/img/spreadsheet-import-example.png';
}

export function shouldHaveResidues(selectedSchemaIds: SelectedSchemaIds): boolean {
  const tagSchema = getTagSchema(selectedSchemaIds);
  return Boolean(tagSchema && TagSchemaUtil.entityTypeSupportsResidues(tagSchema.folderItemType));
}

export function shouldHaveChemicalStructure(selectedSchemaIds: SelectedSchemaIds): boolean {
  const tagSchema = getTagSchema(selectedSchemaIds);
  return Boolean(tagSchema && TagSchemaUtil.entityTypeSupportsChemicalStructure(tagSchema.folderItemType));
}

export function isSequenceSchema(selectedTagSchemaId: string): boolean {
  const tagSchema = getTranslatedTagSchemaFromCache(selectedTagSchemaId);
  return tagSchema != null && TagSchemaUtil.isDNASequenceSchema(tagSchema);
}

/**
 * Returns a new list that only contains the unique objects.
 *
 * Any values with duplicates are not included in the return value. The uniqueness is determined based on the
 * output of the iteratee transformation function.
 */
function filterUniqueObjects<T>(objList: Array<T>, iteratee: (obj: T) => string): Array<T> {
  const seenValues = new Set();
  const duplicateValues = new Set();
  objList.forEach((obj) => {
    if (seenValues.has(iteratee(obj))) {
      duplicateValues.add(iteratee(obj));
    } else {
      seenValues.add(iteratee(obj));
    }
  });
  return objList.filter((obj) => !duplicateValues.has(iteratee(obj)));
}

/**
 * Get a map of schema field by unique and normalized display name.
 *
 * @param fieldsToHide - display names of fields to exclude, e.g. fields whose value is always set
 *  programmatically and should never be guessed (or manually mapped) from a spreadsheet column.
 */
function getFieldByName(
  schema: AnonymousSchema | null,
  fieldsToHide: Array<string> = []
): Map<string, SchemaField> {
  if (!schema) {
    return new Map();
  }
  const fields = ComputedFieldUtil.nonComputedFields(schema).filter(
    (field) => !fieldsToHide.includes(field.displayName)
  );
  // We are filtering out fields with duplicate `displayName` because its uniqueness is not enforced.
  // TODO(BNCH-5152): Remove once we enforce displayName uniqueness
  const fieldsWithUniqueNames = filterUniqueObjects<SchemaField>(fields, (field) => field.displayName);
  return new Map(fieldsWithUniqueNames.map((field) => [normalize(field.displayName), field]));
}

export function getDerivedColumnsWithGuessedTypes(
  derivedColumns: Array<Omit<DerivedColumn, 'type'>>,
  selectedSchemaIds: SelectedSchemaIds
): Array<DerivedColumn> {
  return derivedColumns.map((column) => {
    const result = {...column, type: COLUMN_TYPE.IGNORE};
    if (column.allowedColumnTypes.includes(COLUMN_TYPE.SCHEMA_FIELD)) {
      const tagSchema = getSchemaFromColumnType(COLUMN_TYPE.SCHEMA_FIELD, selectedSchemaIds);
      const compatibleSchemaFieldIds = column.compatibleSchemaFieldIds || [];
      const compatibleSchemaFields = tagSchema
        ? tagSchema.fields.filter((field) => compatibleSchemaFieldIds.includes(field.apiId))
        : [];
      const matchingSchemaFields = compatibleSchemaFields.filter((field) =>
        COLUMN_TRANSFORM_TO_RE[column.columnTransform].test(field.name)
      );
      if (matchingSchemaFields.length > 0) {
        result.type = COLUMN_TYPE.SCHEMA_FIELD;
        result.schemaField = matchingSchemaFields[0];
      }
    }
    return result;
  });
}

export function amendWithGuessedType(
  importType: IMPORT_TYPE,
  validColumnTypes: Array<COLUMN_TYPE>,
  columnNames: Array<string>,
  selectedSchemaIds: SelectedSchemaIds,
  dataTableColumns: {tableColumnNames: Array<string>; dataTableKeyFields: Array<COLUMN_TYPE>},
  dataTypes?: Array<string>,
  getValidSchemaFieldIdsForColumn?: (columnIndex: number) => Array<string>,
  schemaFieldsToHideByColumnType?: Map<COLUMN_TYPE, Array<string>>
): Array<SpreadsheetColumn> {
  const schemaFieldMaps = new Map();
  const validSchemaFieldColumnTypes = validColumnTypes.filter((columnType) =>
    SCHEMA_FIELD_COLUMN_TYPES.includes(columnType)
  );
  validSchemaFieldColumnTypes.forEach((columnType) => {
    schemaFieldMaps.set(
      columnType,
      getFieldByName(
        getSchemaFromColumnType(columnType, selectedSchemaIds),
        schemaFieldsToHideByColumnType?.get(columnType)
      )
    );
  });

  return columnNames.map((name, i) => {
    const validSchemaFieldIds = getValidSchemaFieldIdsForColumn?.(i);
    let guessedType =
      importType === IMPORT_TYPE.UPDATE_MIXTURE_INGREDIENTS
        ? guessTypeForMixtureIngredients(validColumnTypes, name)
        : guessType(validColumnTypes, name, i, schemaFieldMaps, dataTableColumns, validSchemaFieldIds);
    if (!guessedType || !validColumnTypes.includes(guessedType.type)) {
      assert(
        validColumnTypes.includes(COLUMN_TYPE.IGNORE),
        `Must specify IGNORE as a possible column type when a column might not have a guess: ${name}`
      );
      guessedType = {type: COLUMN_TYPE.IGNORE};
    }
    const newColumn: SpreadsheetColumn = {
      name,
      ...guessedType,
      columnMappingSource: ColumnMappingSource.BENCHLING_GUESSED,
    };
    if (dataTypes) {
      newColumn.dataType = dataTypes[i];
    }
    return newColumn;
  });
}

/**
 * Returns true if the column is an IGNORE column that was guessed by Benchling's automatic guessing.
 */
export function isUnconfirmedIgnoredColumn(column: SpreadsheetColumn): boolean {
  return (
    column.type === COLUMN_TYPE.IGNORE && column.columnMappingSource === ColumnMappingSource.BENCHLING_GUESSED
  );
}

/**
 * Confirms that columns marked as IGNORE by Benchling's automatic guessing should remain ignored.
 * Updates the column mapping source from BENCHLING_GUESSED to BENCHLING_GUESS_CONFIRMED
 * to indicate user acknowledgment of the ignored columns.
 */
export function confirmIgnoredColumns(columns: Array<SpreadsheetColumn>): Array<SpreadsheetColumn> {
  return columns.map((column) => {
    if (isUnconfirmedIgnoredColumn(column)) {
      return {
        ...column,
        columnMappingSource: ColumnMappingSource.BENCHLING_GUESS_CONFIRMED,
      };
    }
    return column;
  });
}

export function pluckColumnType(columns: Array<SpreadsheetColumn>): Array<COLUMN_TYPE> {
  return columns.map(({type}) => type);
}

export function getColumnTypeToSendToServer(columnType: COLUMN_TYPE): COLUMN_TYPE {
  // All schema field column types are sent as COLUMN_TYPE.SCHEMA_FIELD to the server.
  return SCHEMA_FIELD_COLUMN_TYPES.includes(columnType) ? COLUMN_TYPE.SCHEMA_FIELD : columnType;
}

/**
 * Returns a map that maps column names that have SCHEMA_FIELD type to the id of the
 * corresponding schema field
 */
export function getSchemaFieldIdsByColumnName(columns: Array<SpreadsheetColumn>): {[name: string]: number} {
  const schemaFieldIdsByColumnName: {[name: string]: number} = {};
  for (const column of columns) {
    if (getColumnTypeToSendToServer(column.type) === COLUMN_TYPE.SCHEMA_FIELD) {
      schemaFieldIdsByColumnName[column.name] = column.schemaField!.id;
    }
  }
  return schemaFieldIdsByColumnName;
}

export function getComplexPolymerComponentColumnMapping(columns: Array<SpreadsheetColumn>): {
  [name: string]: string;
} {
  const complexPolymerColumnMapping: {[name: string]: string} = {};
  for (const column of columns) {
    if (getColumnTypeToSendToServer(column.type) === COLUMN_TYPE.COMPLEX_POLYMER_COMPONENT) {
      complexPolymerColumnMapping[column.name] = column.complexPolymerComponent!.schemaFieldId;
    }
  }
  return complexPolymerColumnMapping;
}

export function getTableColumnNamesByColumnName(columns: Array<SpreadsheetColumn>): {
  [name: string]: string;
} {
  const tableColumnNamesByColumnName: {[name: string]: string} = {};
  for (const column of columns) {
    if ([COLUMN_TYPE.DATA_TABLE_MATCHER, COLUMN_TYPE.DATA_TABLE_VALUE].includes(column.type)) {
      tableColumnNamesByColumnName[column.name] = column.tableColumnName!;
    }
  }
  return tableColumnNamesByColumnName;
}

export function getSampleTypesByColumnName(columns: Array<SpreadsheetColumn>): {
  [name: string]: keyof typeof SAMPLE_TYPE_DISPLAY;
} {
  return objectFromEntries(
    columns
      .filter((column) => column.type === COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD)
      .map((column) => [column.name, column.sampleType!])
  );
}

/**
 * Return the first column with a "duplicate" later in the list.
 */
export function findDuplicateColumn(
  columns: Array<SpreadsheetColumn>,
  isDuplicateFn: (col1: SpreadsheetColumn, col2: SpreadsheetColumn) => boolean
): SpreadsheetColumn | null {
  for (let i = 0; i < columns.length; i++) {
    for (let j = i + 1; j < columns.length; j++) {
      if (isDuplicateFn(columns[i], columns[j])) {
        return columns[i];
      }
    }
  }
  return null;
}

/**
 * Returns the error to display to the user in case of duplicate tags that the columns would create.
 * Returns null if no errors were found.
 */
export function getDuplicateTagNameError(columns: Array<SpreadsheetColumn>): string | null {
  const customFieldNames = columns
    .filter(({type}) => type === COLUMN_TYPE.CUSTOM_FIELD)
    .map(({name}) => name);
  const schemaFieldColumns = columns.filter(
    ({type}) =>
      getColumnTypeToSendToServer(type) === COLUMN_TYPE.SCHEMA_FIELD &&
      type !== COLUMN_TYPE.SAMPLE_GROUP_SCHEMA_FIELD
  );

  const schemaFieldIds = new Set();
  for (const column of schemaFieldColumns) {
    if (column.schemaField == null) {
      continue;
    }
    if (schemaFieldIds.has(column.schemaField.id)) {
      return `Only one "${column.schemaField.name}" schema field is allowed.`;
    } else if (customFieldNames.includes(column.schemaField.name)) {
      return `Schema field "${column.schemaField.name}" conflicts with a custom field with the same name.`;
    } else {
      schemaFieldIds.add(column.schemaField.id);
    }
  }

  return null;
}

export function getDuplicateColumnTypesError(columns: Array<SpreadsheetColumn>): string | null {
  // Returns an error for any column type that only allows a single-value (see
  // benchling/api/bulk_adapters/extract/__init__.py:_transform_single_column_values).
  for (const columnType of Object.values(COLUMN_TYPE)) {
    if (
      !COLUMN_TYPES_ALLOWING_MULTIPLE_VALUES.includes(columnType) &&
      columns.filter(({type}) => type === columnType).length > 1
    ) {
      return `There can be at most one "${COLUMN_TYPE_TO_NAME[columnType] ?? columnType}" column.`;
    }
  }
  return null;
}

/**
 * Returns an error if some but not all of a group of columns that must be selected together, is selected.
 *
 * @param {array} columns - selected columns in the spreadsheet bulk import modal
 * @param {array} dependentColumnTypeGroups - array of arrays, where each element is a group of column types
 *  that must be specified together
 */
export function getDependentColumnTypesError(
  columns: Array<SpreadsheetColumn>,
  dependentColumnTypeGroups: Array<Array<COLUMN_TYPE>>
): string | null {
  const selectedColumnTypes = columns.map(({type}) => type);
  for (const dependentColumnTypes of dependentColumnTypeGroups) {
    if (
      columns.some(({type}) => dependentColumnTypes.includes(type)) &&
      !dependentColumnTypes.every((type) => selectedColumnTypes.includes(type))
    ) {
      const names = dependentColumnTypes.map((type) => `"${COLUMN_TYPE_TO_NAME[type]}"`);

      const conjunction = dependentColumnTypes.length > 2 ? 'all' : 'both';
      return `${listToReadable(names)} must ${conjunction} be specified.`;
    }
  }
  return null;
}

export function canApplyReplaceLinkWithText(errorCode: RowErrorCode): boolean {
  return errorCode !== RowErrorCode.INVALID_SELECTOR_OPTIONS && LINK_TAG_ERROR_CODES.includes(errorCode);
}

export function canUseExistingContainers(errorCode: RowErrorCode): boolean {
  return errorCode === RowErrorCode.FOUND_EXISTING_CONTAINER;
}

export function canUseNonEmptyContainers(errorCode: RowErrorCode): boolean {
  return errorCode === RowErrorCode.NON_EMPTY_CONTAINER;
}

export function canUseSamplesWithStatusWarnings(errorCode: RowErrorCode): boolean {
  return (
    errorCode === RowErrorCode.FOUND_SAMPLE_WITH_NONBLOCKING_STATUS_WARNING ||
    // TODO(BNCH-57841): Drop deprecated value support when most clients have the code that supports NONBLOCKING
    errorCode === RowErrorCode.DEPRECATED_FOUND_SAMPLE_WITH_STATUS_WARNING
  );
}

function getErrors(errorData: SpreadsheetValidationErrorInfo): Array<SpreadsheetValidationError> {
  return Object.values(
    pick(
      errorData,
      'itemNameErrors',
      'tagErrors',
      'registryErrors',
      'registryWarnings',
      'otherErrors',
      'otherWarnings',
      'studyWarnings'
    )
  ).flat();
}

export function hasErrors(errorData: ErrorData): boolean {
  const errors = getErrors(errorData);
  return Boolean(errors.length);
}

function getUnresolvedErrors(
  errorData: SpreadsheetValidationErrorInfo,
  resolvableErrorStatuses: {[key in OPTIONS]?: boolean}
): Array<SpreadsheetValidationError> {
  const {itemNameErrors, tagErrors, registryErrors, otherErrors} = errorData;
  const unresolvedTagErrors = resolvableErrorStatuses[OPTIONS.REPLACE_LINK_WITH_TEXT]
    ? tagErrors.filter((tagError) => !canApplyReplaceLinkWithText(tagError.errorCode))
    : tagErrors;
  const unresolvedItemNameErrors = itemNameErrors.filter((itemNameError) => {
    const isBlocking = !NON_BLOCKING_ERROR_CODES.includes(itemNameError.errorCode);
    switch (itemNameError.errorCode) {
      case RowErrorCode.NON_EMPTY_CONTAINER:
        return isBlocking && !resolvableErrorStatuses[OPTIONS.USE_NON_EMPTY_CONTAINERS];
      case RowErrorCode.FOUND_EXISTING_CONTAINER:
        return isBlocking && !resolvableErrorStatuses[OPTIONS.USE_EXISTING_CONTAINERS];
      case RowErrorCode.FOUND_SAMPLE_WITH_NONBLOCKING_STATUS_WARNING:
      // TODO(BNCH-57841): Drop deprecated value support when most clients have the code that supports NONBLOCKING
      // eslint-disable-next-line no-fallthrough
      case RowErrorCode.DEPRECATED_FOUND_SAMPLE_WITH_STATUS_WARNING:
        return isBlocking && !resolvableErrorStatuses[OPTIONS.USE_SAMPLES_WITH_STATUS_WARNINGS];
      default:
        return isBlocking;
    }
  });
  return getErrors({
    itemNameErrors: unresolvedItemNameErrors,
    tagErrors: unresolvedTagErrors,
    registryErrors,
    registryWarnings: [],
    otherErrors,
    otherWarnings: [],
    studyWarnings: [],
  });
}

export function hasUnresolvedErrors(
  errorData: SpreadsheetValidationErrorInfo,
  resolvableErrorStatuses: {[key in OPTIONS]?: boolean}
): boolean {
  const unresolvedErrors = getUnresolvedErrors(errorData, resolvableErrorStatuses);
  return Boolean(unresolvedErrors.length);
}

export function getRowsToSkip(
  errorData: ErrorData | null,
  resolvableErrorStatuses: {[key in OPTIONS]?: boolean}
): Array<number> {
  if (!errorData || errorData.state !== TaskState.COMPLETED) {
    return [];
  }
  const unresolvedErrors = getUnresolvedErrors(errorData, resolvableErrorStatuses);
  const rowsToSkip = unresolvedErrors.map((error) => error.row);
  // eslint-disable-next-line no-restricted-syntax
  return _.uniq(rowsToSkip);
}

const otherErrorMessageGettersByCode: {
  [key in RowErrorCode]?: (error: SpreadsheetValidationError) => string;
} = {
  [RowErrorCode.INVALID_STRUCTURED_TABLE_TAG_VALUE]: (error: SpreadsheetValidationError) =>
    `Invalid value for field ${error.fieldName}: "${
      error.invalidTypedValue ? formatTypedValue(error.invalidTypedValue) : ''
    }"`,
};

function addItemNameErrorMessages(
  errorsByRow: Map<number, Array<SpreadsheetValidationErrorMessage>>,
  itemNameErrors: Array<SpreadsheetValidationError>,
  resolvableErrorStatuses: {[key in OPTIONS]?: boolean}
): void {
  for (const {row, errorMessage, errorCode} of itemNameErrors) {
    const isResolved =
      (resolvableErrorStatuses[OPTIONS.USE_EXISTING_CONTAINERS] && canUseExistingContainers(errorCode)) ||
      (resolvableErrorStatuses[OPTIONS.USE_NON_EMPTY_CONTAINERS] && canUseNonEmptyContainers(errorCode)) ||
      (resolvableErrorStatuses[OPTIONS.USE_SAMPLES_WITH_STATUS_WARNINGS] &&
        canUseSamplesWithStatusWarnings(errorCode)) ||
      false;
    const isError = !NON_BLOCKING_ERROR_CODES.includes(errorCode);

    if (!errorsByRow.has(row)) {
      errorsByRow.set(row, []);
    }
    const newError: SpreadsheetValidationErrorMessage = {
      message: errorMessage,
      resolved: isResolved,
      icon: isError ? 'error' : 'warning',
    };
    const rowErrors = errorsByRow.get(row);
    if (rowErrors) {
      rowErrors.push(newError);
    } else {
      errorsByRow.set(row, [newError]);
    }
  }
}

function addTagErrorMessages(
  errorsByRow: Map<number, Array<SpreadsheetValidationErrorMessage>>,
  tagErrors: Array<SpreadsheetValidationError>,
  resolvableErrorStatuses: {[key in OPTIONS]?: boolean}
): void {
  tagErrors?.forEach(({row, errorMessage, errorCode}) => {
    const isResolved =
      (resolvableErrorStatuses[OPTIONS.REPLACE_LINK_WITH_TEXT] && canApplyReplaceLinkWithText(errorCode)) ||
      false;

    if (!errorsByRow.has(row)) {
      errorsByRow.set(row, []);
    }
    errorsByRow.get(row)!.push({
      message: errorMessage,
      resolved: isResolved,
      icon: 'error',
    });
  });
}

function addOtherErrorMessages(
  errorsByRow: Map<number, Array<SpreadsheetValidationErrorMessage>>,
  otherErrors: Array<SpreadsheetValidationError>
): void {
  otherErrors?.forEach((error) => {
    const {row, errorCode, errorMessage} = error;
    let message;
    if (otherErrorMessageGettersByCode[errorCode]) {
      message = otherErrorMessageGettersByCode[errorCode](error);
    } else {
      message = errorMessage;
    }
    if (!errorsByRow.has(row)) {
      errorsByRow.set(row, []);
    }
    errorsByRow.get(row)!.push({
      message,
      resolved: false,
      icon: 'error',
    });
  });
}

function addStudyWarningMessages(
  errorsByRow: Map<number, Array<SpreadsheetValidationErrorMessage>>,
  studyWarnings: Array<SpreadsheetValidationError>
): void {
  // TODO(BNCH-105566): Implement a separate UI for displaying study warnings
  studyWarnings?.forEach(({row, errorMessage}) => {
    if (!errorsByRow.has(row)) {
      errorsByRow.set(row, []);
    }
    errorsByRow.get(row)!.push({
      message: errorMessage,
      resolved: false,
      icon: 'warning',
    });
  });
}

function addOtherWarningMessages(
  errorsByRow: Map<number, Array<SpreadsheetValidationErrorMessage>>,
  otherWarnings: Array<SpreadsheetValidationError>
): void {
  otherWarnings?.forEach((error) => {
    if (!errorsByRow.has(error.row)) {
      errorsByRow.set(error.row, []);
    }
    errorsByRow.get(error.row)!.push({
      message: error.errorMessage,
      resolved: false,
      icon: 'warning',
    });
  });
}

export interface SpreadsheetValidationErrorMessage {
  message: string;
  icon: BenchlingIconName;
  resolved: boolean;
}

export function getErrorMessages(
  errorData: SpreadsheetValidationErrorInfo,
  resolvableErrorStatuses: {[key in OPTIONS]?: boolean}
): Map<number, Array<SpreadsheetValidationErrorMessage>> {
  const {
    itemNameErrors,
    tagErrors,
    registryErrors,
    registryWarnings,
    otherErrors,
    otherWarnings,
    studyWarnings,
  } = errorData;

  const errorsByRow = new Map();
  addItemNameErrorMessages(errorsByRow, itemNameErrors, resolvableErrorStatuses);
  addTagErrorMessages(errorsByRow, tagErrors, resolvableErrorStatuses);
  addOtherErrorMessages(errorsByRow, otherErrors);
  addOtherWarningMessages(errorsByRow, otherWarnings);
  addStudyWarningMessages(errorsByRow, studyWarnings);

  // Only show registry errors or warnings if all other errors have been resolved
  if (
    !hasUnresolvedErrors(
      {
        itemNameErrors,
        registryErrors: [],
        registryWarnings: [],
        otherErrors,
        otherWarnings,
        tagErrors,
        studyWarnings,
      },
      resolvableErrorStatuses
    )
  ) {
    registryWarnings.forEach(({row, errorCode, errorMessage}) => {
      assert(errorCode === RowErrorCode.REGISTRY_ERROR, 'Expected to receive RowErrorCode.REGISTRY_ERROR');
      if (!errorsByRow.has(row)) {
        errorsByRow.set(row, []);
      }
      errorsByRow.get(row).push({message: errorMessage, icon: 'warning', resolved: false});
    });
    registryErrors.forEach(({row, errorCode, errorMessage}) => {
      assert(errorCode === RowErrorCode.REGISTRY_ERROR, 'Expected to receive RowErrorCode.REGISTRY_ERROR');
      if (!errorsByRow.has(row)) {
        errorsByRow.set(row, []);
      }
      errorsByRow.get(row).push({message: errorMessage, icon: 'error', resolved: false});
    });
  }
  return errorsByRow;
}

export function getFormattedErrors(
  errorData: SpreadsheetValidationErrorInfo,
  resolvableErrorStatuses: {[key in OPTIONS]?: boolean}
): Array<{row: number; unresolvedMessage: Array<JSX.Element>; resolvedMessage: Array<JSX.Element>}> {
  const errorMessages = getErrorMessages(errorData, resolvableErrorStatuses);
  const formattedErrors = [];
  for (const [row, rowErrors] of errorMessages) {
    const [resolvedRowErrors, unresolvedRowErrors] = _.partition(rowErrors, _.property('resolved')) as [
      Array<SpreadsheetValidationErrorMessage>,
      Array<SpreadsheetValidationErrorMessage>,
    ];
    formattedErrors.push({
      row,
      unresolvedMessage: unresolvedRowErrors.map((error, i) => (
        <div key={`${row}|${i}`}>
          {getMessageSpan(error.message, error.icon)}
          <br />
        </div>
      )),
      resolvedMessage: resolvedRowErrors.map((error, i) => (
        <div key={`${row}|${i}`}>
          {getMessageSpan(error.message, error.icon)}
          <br />
        </div>
      )),
    });
  }
  return _.sortBy(formattedErrors, 'row');
}

// If this is modified, please update the test version of this function. generateColumnCoordinateLetters in browser-tests/lib/notebook/utils/generateColumnCoordinateLetters.ts
export function colNumToExcelId(i: number): string {
  // Convert a column number into the column ID used by Excel (e.g. 0 is A, 1 is B, 26 is AA, etc.)
  const CAPITAL_A_CODE = 65;

  let colId = '';
  let dividend = i + 1;
  let remainder;

  // Excel column IDs don't map neatly onto base 26 because 'A' is different than 'AA' (so there's no 0
  // equivalent). We adjust the typical int -> string algorithm by subtracting the remainder from the
  // dividend every cycle.
  while (dividend > 0) {
    remainder = (dividend - 1) % 26;
    colId = String.fromCharCode(CAPITAL_A_CODE + remainder) + colId;
    dividend = Math.floor((dividend - remainder) / 26);
  }
  return colId;
}

function isCreatingEntities(importType: IMPORT_TYPE): boolean {
  switch (importType) {
    case IMPORT_TYPE.INVENTORY_FILES:
    case IMPORT_TYPE.REGISTRY_ENTITIES:
    case IMPORT_TYPE.TRIAL_SAMPLES:
      return true;
    default:
      return false;
  }
}

export function getFieldName(
  columnType: COLUMN_TYPE,
  selectedSchemaIds: SelectedSchemaIds,
  importType: IMPORT_TYPE,
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes
): string {
  if (columnType === COLUMN_TYPE.FILENAME && isCreatingEntities(importType)) {
    if (schemalessCreationEntityType) {
      return `${capitalize(typeToName(schemalessCreationEntityType))} name`;
    } else if (importType === IMPORT_TYPE.TRIAL_SAMPLES) {
      return 'Trial sample name';
    }
    return 'Entity name';
  }

  if (columnType === COLUMN_TYPE.RESIDUES) {
    const tagSchema = getTagSchema(selectedSchemaIds);
    const selectedEntityType = tagSchema?.folderItemType ?? schemalessCreationEntityType;
    if (selectedEntityType) {
      return benchlingUtil.pluralize(0, capitalize(FileUtil.getResidueWord(selectedEntityType)));
    }
  }

  if (
    [COLUMN_TYPE.STORABLE_NAME, COLUMN_TYPE.BARCODE].includes(columnType) &&
    isCreatingEntities(importType)
  ) {
    return capitalize(`Container ${COLUMN_TYPE_TO_NAME[columnType]}`.toLowerCase());
  }

  if (columnType === COLUMN_TYPE.SCHEMA_FIELD && importType === IMPORT_TYPE.TRIAL_SAMPLES) {
    return 'Trial sample field';
  }

  return COLUMN_TYPE_TO_NAME[columnType]!;
}

export function getFieldHelpText(
  columnType: COLUMN_TYPE,
  selectedSchemaIds: SelectedSchemaIds,
  entityType?: string
): string {
  if (columnType === COLUMN_TYPE.RESIDUES) {
    const tagSchema = getTagSchema(selectedSchemaIds);
    const itemType = tagSchema?.folderItemType ?? entityType;
    if (itemType) {
      return (
        `The ${benchlingUtil.pluralize(0, FileUtil.getResidueWord(itemType)!)} for the ` +
        `${typeToName(itemType)}.`
      );
    }
  }
  assertAndContinue(
    COLUMN_TYPE_TO_HELP_TEXT.hasOwnProperty(columnType),
    `Expected a description for column type ${columnType}`
  );
  return COLUMN_TYPE_TO_HELP_TEXT[columnType]!;
}

export function getErrorForCreatingPolymer(
  columns: Array<SpreadsheetColumn>,
  shouldPolymerHaveResidues: boolean,
  canPolymerHaveMods: boolean
): string | null {
  const numBasesColumns = columns.filter(({type}) => type === COLUMN_TYPE.RESIDUES).length;
  const numHELMColumns = columns.filter(({type}) => type === COLUMN_TYPE.HELM).length;
  const numIDTColumns = columns.filter(({type}) => type === COLUMN_TYPE.IDT).length;
  const numCustomColumns = columns.filter(({type}) => type === COLUMN_TYPE.CUSTOM_MOD_SYNTAX).length;
  if (canPolymerHaveMods) {
    const validColumns = [
      '"Bases"',
      '"HELM"',
      BenchlingConfig.MOLBIO_ENABLE_IDT_SYNTAX && '"IDT"',
      BenchlingConfig.MODBIO_ENABLE_CUSTOM_SYNTAXES && '"Custom Notation"',
    ].filter((c) => Boolean(c)) as Array<string>;
    // We can safely assume that if a schema can have mods, then it should also have residues.
    if (benchlingUtil.sum([numBasesColumns, numHELMColumns, numIDTColumns, numCustomColumns]) === 0) {
      return `One column of type ${listToReadable(validColumns, 'or')} is required.`;
    } else if (numBasesColumns > 1) {
      return 'There cannot be more than one "Bases" field.';
    } else if (numHELMColumns > 1) {
      return 'There cannot be more than one "HELM" field.';
    } else if (numIDTColumns > 1) {
      return 'There cannot be more than one "IDT" field.';
    } else if (numCustomColumns > 1) {
      return 'There cannot be more than one "Custom Notation" field.';
    } else if (benchlingUtil.sum([numBasesColumns, numHELMColumns, numIDTColumns, numCustomColumns]) > 1) {
      return `Only one of columns ${listToReadable(validColumns)} can be specified.`;
    }
  } else if (shouldPolymerHaveResidues) {
    if (numBasesColumns === 0) {
      return 'A column of type "Bases" is required.';
    } else if (numBasesColumns > 1) {
      return 'There cannot be more than one "Bases" field.';
    }
  }
  return null;
}

export function getErrorForChemicalStructureColumns(
  columns: Array<SpreadsheetColumn>,
  shouldHaveChemicalStructure: boolean,
  isCreationFlow: boolean = false
): string | null {
  if (shouldHaveChemicalStructure) {
    const numSMILESColumns = columns.filter(({type}) => type === COLUMN_TYPE.SMILES).length;
    const numMolfileColumns = columns.filter(({type}) => type === COLUMN_TYPE.MOLFILE).length;
    if (isCreationFlow && benchlingUtil.sum([numSMILESColumns, numMolfileColumns]) === 0) {
      return 'One column of type "SMILES" or "Molfile" is required.';
    } else if (numSMILESColumns > 1) {
      return 'There cannot be more than one "SMILES" field.';
    } else if (numMolfileColumns > 1) {
      return 'There cannot be more than one "Molfile" field.';
    } else if (benchlingUtil.sum([numSMILESColumns, numMolfileColumns]) > 1) {
      return 'Only one of columns "SMILES" and "Molfile" can be specified.';
    }
  }
  return null;
}

export function getPolymerColumnTypes(
  shouldPolymerHaveResidues: boolean,
  canPolymerHaveMods: boolean
): Array<COLUMN_TYPE> {
  return compact([
    shouldPolymerHaveResidues ? COLUMN_TYPE.RESIDUES : null,
    canPolymerHaveMods ? COLUMN_TYPE.HELM : null,
    canPolymerHaveMods && BenchlingConfig.MOLBIO_ENABLE_IDT_SYNTAX ? COLUMN_TYPE.IDT : null,
    canPolymerHaveMods &&
    BenchlingConfig.MODBIO_ENABLE_CUSTOM_SYNTAXES &&
    Object.keys(BenchlingConfig.MODBIO_CUSTOM_SYNTAX_NAMES_BY_ID).length > 0
      ? COLUMN_TYPE.CUSTOM_MOD_SYNTAX
      : null,
  ]);
}

export function getMoleculeColumnTypes(shouldHaveChemicalStructure: boolean): Array<COLUMN_TYPE> {
  return BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE && shouldHaveChemicalStructure
    ? MOLECULE_COLUMN_TYPES
    : [];
}

export function shouldHaveComplexPolymerStructure(
  selectedSchemaIds: SelectedSchemaIds,
  complexPolymerSchemaConfigs?: Array<ComplexPolymerSchemaConfigGraphQL>
): boolean {
  return (
    BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES &&
    selectedSchemaIds.tagSchemaId != null &&
    complexPolymerSchemaConfigs != null &&
    complexPolymerSchemaConfigs
      .map((schemaConfig) => schemaConfig.schema.id)
      .includes(selectedSchemaIds.tagSchemaId)
  );
}

export function getComplexPolymerColumnTypes(
  selectedSchemaIds: SelectedSchemaIds,
  complexPolymerSchemaConfigs?: Array<ComplexPolymerSchemaConfigGraphQL>
): Array<COLUMN_TYPE> {
  if (!shouldHaveComplexPolymerStructure(selectedSchemaIds, complexPolymerSchemaConfigs)) {
    return [];
  }
  const matchingSchemaConfig = complexPolymerSchemaConfigs!.filter(
    (schemaConfig) => schemaConfig.schema.id === selectedSchemaIds.tagSchemaId
  )[0];
  if (isOligoDuplexConfigGraphQLType(matchingSchemaConfig)) {
    return [...COMPLEX_POLYMER_COLUMN_TYPES, COLUMN_TYPE.OLIGO_DUPLEX_OFFSET];
  }
  return COMPLEX_POLYMER_COLUMN_TYPES;
}

export function getRequiredFieldError(
  requiredColumnTypes: Array<COLUMN_TYPE>,
  columns: Array<SpreadsheetColumn>,
  selectedSchemaIds: SelectedSchemaIds,
  importType: IMPORT_TYPE,
  schemalessCreationEntityType?: SchemalessBulkImportSupportedOligoAwareTypes
): string | null {
  for (const columnType of requiredColumnTypes) {
    let error = null;
    const numColumns = columns.filter(({type}) => type === columnType).length;
    const fieldName = getFieldName(columnType, selectedSchemaIds, importType, schemalessCreationEntityType);

    if (numColumns === 0) {
      error = `A column of type "${fieldName}" is required.`;
    } else if (numColumns > 1) {
      error = `There must be exactly one "${fieldName}" field.`;
    }
    if (error) {
      return error;
    }
  }
  return null;
}

/**
 * Validates that the required schema fields are present in the given columns.
 *
 * @param columns - An array of `SpreadsheetColumn` objects representing the columns in the spreadsheet.
 * @param requiredSchemaFieldDisplayNamesByColumnType - A map where the keys are `COLUMN_TYPE` values and the
 *        values are arrays of required schema field display names for each column type.
 * @returns A string containing an error message if a required schema field is missing or duplicated, or null
 *        if all required schema fields are present.
 */
export function getRequiredSchemaFieldError(
  columns: Array<SpreadsheetColumn>,
  requiredSchemaFieldDisplayNamesByColumnType: Map<COLUMN_TYPE, Array<string>>
): string | null {
  const columnTypes = requiredSchemaFieldDisplayNamesByColumnType.keys();
  for (const columnType of columnTypes) {
    const isSchemaField = SCHEMA_FIELD_COLUMN_TYPES.includes(columnType);
    assertAndContinue(
      SCHEMA_FIELD_COLUMN_TYPES.includes(columnType),
      `Expected only schema field column types, instead got ${columnType}`
    );
    if (!isSchemaField) {
      continue;
    }

    const requiredSchemaFieldDisplayNames = requiredSchemaFieldDisplayNamesByColumnType.get(columnType)!;
    for (const requiredSchemaFieldDisplayName of requiredSchemaFieldDisplayNames) {
      let error = null;

      const numColumns = columns
        .filter(({type}) => type === columnType)
        .filter(({schemaField}) => schemaField?.displayName === requiredSchemaFieldDisplayName).length;

      if (numColumns === 0) {
        error = `A column of type "${requiredSchemaFieldDisplayName}" is required.`;
      } else if (numColumns > 1) {
        error = `There must be exactly one "${requiredSchemaFieldDisplayName}" field.`;
      }
      if (error) {
        return error;
      }
    }
  }

  return null;
}

export function getSampleControlRolesPropagationWarning(
  columns: Array<SpreadsheetColumn> | null,
  importType: IMPORT_TYPE
): string | null {
  if (
    BenchlingConfig.INVENTORY_ENABLE_SAMPLE_CONTROL_FEATURES !== SampleControlsFeatureLevel.V1 ||
    columns == null
  ) {
    return null;
  }
  if (!columns.some(({type}) => TRANSFER_PRIMARY_STORABLE_INPUT_COLUMN_TYPES.includes(type))) {
    return null;
  }

  const destinationPhrase =
    importType === IMPORT_TYPE.TRANSFER_STORABLES
      ? 'those of the destination containers'
      : 'the created containers';
  return `Any sample owners and ${getRestrictedSampleUsersDisplayText()} of the source containers will be added to ${destinationPhrase}.`;
}

export function isImportTypeForSamples(importType: IMPORT_TYPE): boolean {
  return [IMPORT_TYPE.SAMPLES, IMPORT_TYPE.UPDATE_SAMPLES].includes(importType);
}

export function isForUpdateFields(importType: IMPORT_TYPE): boolean {
  return [
    IMPORT_TYPE.UPDATE_BIOENTITIES,
    IMPORT_TYPE.UPDATE_SAMPLES,
    IMPORT_TYPE.UPDATE_CONTAINERS,
    IMPORT_TYPE.UPDATE_PLATES,
    IMPORT_TYPE.UPDATE_BOXES,
    IMPORT_TYPE.UPDATE_LOCATIONS,
  ].includes(importType);
}

export function isForUpdateFieldsWithCustomFields(importType: IMPORT_TYPE): boolean {
  return [IMPORT_TYPE.UPDATE_BIOENTITIES, IMPORT_TYPE.UPDATE_SAMPLES].includes(importType);
}

export function doesImportTypeSupportClearingSampleRoles(importType: IMPORT_TYPE): boolean {
  return [IMPORT_TYPE.UPDATE_CONTAINERS, IMPORT_TYPE.TRANSFER_STORABLES].includes(importType);
}

export function canOverrideTimeout(): boolean {
  const currentUser = EntityStore.currentUser();
  return (
    preferences.get(SpreadsheetBulkImportPreferences.CAN_OVERRIDE_TIMEOUT) ||
    (currentUser && currentUser.admin)
  );
}

export function getDerivedColumnsMapping(derivedColumns: Array<DerivedColumn>): DerivedColumnsMapping {
  const derivedColumnsMapping: DerivedColumnsMapping = {};
  for (const derivedColumn of derivedColumns) {
    derivedColumnsMapping[derivedColumn.columnTransform] = derivedColumn.type;
  }
  return derivedColumnsMapping;
}

export function shouldCheckDerivedColumns(columns: Array<SpreadsheetColumn>): boolean {
  return columns.some(({type}) => COLUMN_TYPES_TO_CHECK_FOR_DERIVED_COLUMNS.includes(type));
}

export function getImportEntityType(
  selectedSchemaIds: SelectedSchemaIds,
  schemalessCreationEntityType: SchemalessBulkImportSupportedOligoAwareTypes | null
): OligoAwareFolderItemType | null {
  const tagSchema = getTagSchema(selectedSchemaIds);
  const tagSchemaOligoAwareFolderItemType: OligoAwareFolderItemType | null =
    tagSchema != null ? getTagSchemaOligoAwareFolderItemType(tagSchema) : null;
  return schemalessCreationEntityType ?? tagSchemaOligoAwareFolderItemType;
}
